/* =============================================================
   A page that throws while it draws shows this instead of a blank
   screen (audit 0.18). App.tsx keys it on the address, so moving to
   another page starts it afresh; the header, footer and every other
   page keep working. A page that failed because this browser holds an
   old copy of the app (after a deploy) is fixed by the reload button.
   ============================================================ */

import { Component, type ErrorInfo, type ReactNode } from 'react';

export class PageErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[app] page failed to draw:', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="eyebrow mb-4">Something went wrong</div>
        <h1 className="editorial-h1 mb-4">
          This page <em className="italic text-accent">didn't load.</em>
        </h1>
        <p className="font-editorial italic text-[16px] text-ink-soft mb-8">
          Reloading usually fixes it. Anything you'd saved is still in your account.
        </p>
        <div className="flex justify-center gap-6">
          <button type="button" className="btn-text" onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <a href="/account" className="btn-text">
            Go to your account
          </a>
        </div>
      </div>
    );
  }
}
